import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import i18n from 'I18n';

import CustomSwitchButton from 'components/base/CustomSwitchButton';
import CustomTitleWithInput from 'components/base/CustomTitleWithInput';
import { CustomTitleLabel } from 'components/earning/CustomBaseComponments';

import { CARD_BACKGROUND_TYPE, DISPLAY_CARD_BACKGROUND_TYPE, LanguageConfig, noShopify, noWebapp, onlyHaveWebapp } from 'config/CustomEnums';
import { SectionTopTitle } from 'components/base/CommonComponent';
import { LevelErrorHandleFields } from 'containers/engagement/level/LevelErrorHandle';
import { hasError } from 'components/base/ErrorFieldMessage';
import CustomRadios from 'components/base/CustomRadios';
import ImageUploader from 'components/base/ImageUploader';
import LevelReferenceModal from 'containers/engagement/level/LevelReferenceModal';
import LevelBackgroundPreview, { previewTypes } from 'components/level/LevelBackgroundPreview';
import LevelLandscape from './LevelLandscape';
import LevelPortrait from './LevelPortrait';
import './LevelGeneralSection.scss';

const PreviewButton = ({
  disabled,
  type,
  openPreviewModal,
}) => (
  <div>
    <button
      disabled={disabled}
      type="button"
      className="custom-upload-btn preview-button"
      onClick={() => {
        openPreviewModal(type);
      }}
    >
      Preview
    </button>
  </div>
)

const AppBackgroundImageArea = ({
  openPreviewModal,
}) => {
  const {
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const cardLandscapeBackgroundImageInAppField = "cardLandscapeBackgroundImageInApp";
  const cardPortraitBackgroundImageInAppField = "cardPortraitBackgroundImageInApp";

  const watchCardLandscapeBackgroundImageInApp = watch(cardLandscapeBackgroundImageInAppField);
  const watchCardPortraitBackgroundImageInApp = watch(cardPortraitBackgroundImageInAppField);

  if (onlyHaveWebapp) {
    return null;
  };

  return (
    <div className="content-area">
      <CustomTitleLabel title="Background image for card in landscape (app)" />
      <div className="second-section_description">
        Suggested Image Size: 2048px * 1074px (Less than 2MB, support JPG, PNG and GIF only)
      </div>
      <ImageUploader
        images={watchCardLandscapeBackgroundImageInApp ? [watchCardLandscapeBackgroundImageInApp] : []}
        aspect={2048 / 1074}
        maxImageNum={1}
        minWidth={1024}
        minHeight={537}
        language={LanguageConfig.english}
        onImageStateChange={(data) => {
          setValue(cardLandscapeBackgroundImageInAppField, data?.[0], { shouldDirty: true });
        }}
        uploadImageClicked={() => { }}
        errorMessage={
          hasError(errors, cardLandscapeBackgroundImageInAppField)
            ? LevelErrorHandleFields.image
            : ""
        }
      />
      <PreviewButton
        disabled={!watchCardLandscapeBackgroundImageInApp}
        type={previewTypes.appLandscape}
        openPreviewModal={openPreviewModal}
      />

      <CustomTitleLabel title="Background image for card in portrait (app)" />
      <div className="second-section_description">
        Suggested Image Size: 1920px * 3000px (Less than 2MB, support JPG, PNG and GIF only)
      </div>
      <ImageUploader
        images={watchCardPortraitBackgroundImageInApp ? [watchCardPortraitBackgroundImageInApp] : []}
        aspect={1920 / 3000}
        maxImageNum={1}
        minWidth={480}
        minHeight={750}
        language={LanguageConfig.english}
        onImageStateChange={(data) => {
          setValue(cardPortraitBackgroundImageInAppField, data?.[0], { shouldDirty: true });
        }}
        uploadImageClicked={() => { }}
        errorMessage={
          hasError(errors, cardPortraitBackgroundImageInAppField)
            ? LevelErrorHandleFields.image
            : ""
        }
      />
      <PreviewButton
        disabled={!watchCardPortraitBackgroundImageInApp}
        type={previewTypes.appPortrait}
        openPreviewModal={openPreviewModal}
      />
    </div>
  )
}

const WebappBackgroundImageArea = ({
  openPreviewModal,
}) => {
  const [showModal, setShowModal] = useState(false);

  const {
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const cardLandscapeBackgroundImageInWebField = "cardLandscapeBackgroundImageInWeb";
  const cardPortraitBackgroundImageInWebField = "cardPortraitBackgroundImageInWeb";

  const watchCardLandscapeBackgroundImageInWeb = watch(cardLandscapeBackgroundImageInWebField);
  const watchCardPortraitBackgroundImageInWeb = watch(cardPortraitBackgroundImageInWebField);

  if (noShopify && noWebapp) {
    return null;
  };

  return (
    <div className="content-area">
      <div>
        <CustomTitleLabel title="Background image for card in landscape (website)" />

        <label
          className='create-section-label level-website-landscape-see-reference'
          onClick={() => { setShowModal(true) }}
        >
          See reference
        </label>
      </div>
      <div className="second-section_description">
        Suggested Image Size: 1024px * 553px (Less than 2MB, support JPG, PNG and GIF only)
      </div>
      <ImageUploader
        images={watchCardLandscapeBackgroundImageInWeb ? [watchCardLandscapeBackgroundImageInWeb] : []}
        aspect={1024 / 553}
        maxImageNum={1}
        minWidth={1024}
        minHeight={553}
        language={LanguageConfig.english}
        onImageStateChange={(data) => {
          setValue(cardLandscapeBackgroundImageInWebField, data?.[0], { shouldDirty: true });
        }}
        uploadImageClicked={() => { }}
        errorMessage={
          hasError(errors, cardLandscapeBackgroundImageInWebField)
            ? LevelErrorHandleFields.image
            : ""
        }
        cropExtraAreaAspect={927 / 213}
        cropInfo={"The entire cropped area is the desktop responsive size display area.\nThe red area is the mobile responsive size display area."}
      />
      <PreviewButton
        disabled={!watchCardLandscapeBackgroundImageInWeb}
        type={previewTypes.webLandscape}
        openPreviewModal={openPreviewModal}
      />

      <CustomTitleLabel title="Background image for card in portrait (website)" />
      <div className="second-section_description">
        Suggested Image Size: 960px * 1500px (Less than 2MB, support JPG, PNG and GIF only)
      </div>
      <ImageUploader
        images={watchCardPortraitBackgroundImageInWeb ? [watchCardPortraitBackgroundImageInWeb] : []}
        aspect={960 / 1500}
        maxImageNum={1}
        minWidth={480}
        minHeight={750}
        language={LanguageConfig.english}
        onImageStateChange={(data) => {
          setValue(cardPortraitBackgroundImageInWebField, data?.[0], { shouldDirty: true });
        }}
        uploadImageClicked={() => { }}
        errorMessage={
          hasError(errors, cardPortraitBackgroundImageInWebField)
            ? LevelErrorHandleFields.image
            : ""
        }
      />
      <PreviewButton
        disabled={!watchCardPortraitBackgroundImageInWeb}
        type={previewTypes.webPortrait}
        openPreviewModal={openPreviewModal}
      />

      <LevelReferenceModal
        show={showModal}
        setShow={setShowModal}
      />
    </div>
  )
}

const LevelGeneralSection = ({
  language,
}) => {
  const [showPreview, setShowPreview] = useState(false);
  const [previewType, setPreviewType] = useState(null);

  const openPreviewModal = (type) => {
    setPreviewType(type);
    setShowPreview(true);
  };

  const {
    formState: { errors },
    setValue,
    watch,
  } = useFormContext();

  const levelNameField = `translations.${language}.levelName`;
  const foregroundElementColorField = "displayForegroundElementColor";
  const membershipCardBackgroundTypeField = "membershipCardBackgroundType";
  const membershipCardBackgroundColorField = "displayMembershipCardBackgroundColor";
  const hideLevelDetailsAndUpgradeTipsField = "hideLevelDetailsAndUpgradeTips";

  const watchLevelName = watch(levelNameField);
  const watchForegroundElementColor = watch(foregroundElementColorField);
  const watchMembershipCardBackgroundType = watch(membershipCardBackgroundTypeField);
  const watchMembershipCardBackgroundColor = watch(membershipCardBackgroundColorField);
  const watchHideLevelDetailsAndUpgradeTips = watch(hideLevelDetailsAndUpgradeTipsField);

  if (language !== LanguageConfig.english) {
    return (
      <>
        <SectionTopTitle title={i18n.t("general", { locale: language })} />
        <CustomTitleWithInput
          title={i18n.t('name', { locale: language })}
          defaultValue={watchLevelName}
          setValue={(value) => {
            setValue(levelNameField, value, { shouldDirty: true, });
          }}
          error={{
            id: levelNameField,
            error: hasError(errors, levelNameField),
            message: LevelErrorHandleFields.name,
          }}
        />
      </>
    )
  }

  return (
    <>
      <div className='level-general-section-container'>
        <div>
          <SectionTopTitle title="General" />
          <CustomTitleWithInput
            title={i18n.t('name', { locale: language })}
            defaultValue={watchLevelName}
            setValue={(value) => {
              setValue(levelNameField, value, { shouldDirty: true, });
            }}
            error={{
              id: levelNameField,
              error: hasError(errors, levelNameField),
              message: LevelErrorHandleFields.name,
            }}
          />

          <CustomTitleWithInput
            title={"Foreground element color (hex)"}
            defaultValue={watchForegroundElementColor}
            setValue={(value) => {
              setValue(foregroundElementColorField, value, { shouldDirty: true, });
            }}
            per="#"
            unit={
              <div
                className='foreground-element-color-example'
                style={{ backgroundColor: `#${watchForegroundElementColor}` }}
              />
            }
            customLineClass="foreground-element-color-input"
            error={{
              id: foregroundElementColorField,
              error: hasError(errors, foregroundElementColorField),
              message: LevelErrorHandleFields.color,
            }}
          />

          <CustomTitleLabel title="Background of membership card" />
          <div className="d-flex">
            <CustomRadios
              default={watchMembershipCardBackgroundType || CARD_BACKGROUND_TYPE.singleColor}
              onChange={(value) => {
                setValue(membershipCardBackgroundTypeField, value, { shouldDirty: true })
              }}
              options={[
                {
                  label: DISPLAY_CARD_BACKGROUND_TYPE[CARD_BACKGROUND_TYPE.singleColor],
                  value: CARD_BACKGROUND_TYPE.singleColor
                },
                {
                  label: DISPLAY_CARD_BACKGROUND_TYPE[CARD_BACKGROUND_TYPE.image],
                  value: CARD_BACKGROUND_TYPE.image,
                },
              ]}
            />
          </div>

          {watchMembershipCardBackgroundType === CARD_BACKGROUND_TYPE.image
            ? <>
              <AppBackgroundImageArea openPreviewModal={openPreviewModal} />
              <WebappBackgroundImageArea openPreviewModal={openPreviewModal} />
            </>
            : <CustomTitleWithInput
              title={"Background color (hex)"}
              defaultValue={watchMembershipCardBackgroundColor}
              setValue={(value) => {
                setValue(membershipCardBackgroundColorField, value, { shouldDirty: true, });
              }}
              per="#"
              unit={
                <div
                  className='foreground-element-color-example'
                  style={{ backgroundColor: `#${watchMembershipCardBackgroundColor}` }}
                />
              }
              customLineClass="foreground-element-color-input"
              error={{
                id: membershipCardBackgroundColorField,
                error: hasError(errors, membershipCardBackgroundColorField),
                message: LevelErrorHandleFields.color,
              }}
            />
          }

          <div className='hide-level-details-container'>
            <CustomTitleLabel title="Hide level details & upgrade tips on membership card" />
            <CustomSwitchButton
              defaultChecked={watchHideLevelDetailsAndUpgradeTips}
              onChange={(isChecked) => {
                setValue(hideLevelDetailsAndUpgradeTipsField, isChecked, { shouldDirty: true });
              }}
            />
          </div>
        </div>

        <div className='level-general-section-right-section'>
          <LevelLandscape isNormal />
          <LevelPortrait isNormal />
        </div>
      </div>

      <LevelBackgroundPreview
        show={showPreview}
        setShow={setShowPreview}
        previewType={previewType}
      />
    </>
  );
}

export default LevelGeneralSection;
